import { PayloadAction } from '@reduxjs/toolkit';
import { call, put, select } from 'redux-saga/effects';
import {
  screenshareDeleted,
  selectScreenshareStreams,
} from 'features/layout/features/order/orderSlice';
import { updateContentSizeSaga } from 'features/layout/sagas/updateContentSizeSaga';
import { GridPanelName } from 'features/layout/features/content/types';
import { SourceDetails } from 'features/layout/types';
import { eventBus } from 'utils/eventBus';
import { User } from 'features/users/types';
import { ScreenshareStopped } from 'features/screenshare/types';
import { selectShowAllScreens, showAllScreensChanged } from 'features/screenshare/screenshareSlice';
import { contentBoard } from 'features/content-whiteboard/utils/contentBoardStateManager';
import { openLastActiveScreenshareSaga } from 'features/screenshare/sagas/openLastActiveScreenshareSaga';
import { cleanupScreenshareStreamsSaga } from 'features/screenshare/sagas/cleanupScreenshareStreamsSaga';
import { clearLockedContentSaga } from 'features/layout/features/content/sagas/clearLockedContentSaga';
import { gridPanelVoided, selectGridPanel } from 'features/layout/features/content/contentSlice';

export function* onScreensharingStopSaga(action: PayloadAction<ScreenshareStopped>) {
  const { id: userId, feedId, streams } = action.payload;

  const stoppedStream: SourceDetails = {
    userId,
    feedId,
    kind: 'local',
  };

  yield call(clearLockedContentSaga, { type: 'screenshare', id: feedId });

  yield call(updateContentSizeSaga, stoppedStream);

  yield put(screenshareDeleted(stoppedStream));

  const remainingScreenshares: (SourceDetails & { user: User })[] =
    yield select(selectScreenshareStreams);

  const currentOpenedPanel: GridPanelName = yield select(selectGridPanel);

  if (remainingScreenshares.length === 0) {
    yield put(gridPanelVoided('screenshare'));

    if (contentBoard.type === 'screenshare') {
      yield call(contentBoard.clean);
      contentBoard.type = 'file';
      contentBoard.id = null;
    }
  } else {
    const showAllScreens: boolean = yield select(selectShowAllScreens);

    if (
      showAllScreens &&
      currentOpenedPanel === 'screenshare' &&
      remainingScreenshares.length === 1
    ) {
      yield put(showAllScreensChanged(false));
      yield call(openLastActiveScreenshareSaga);
    }

    if (!showAllScreens && currentOpenedPanel === 'screenshare') {
      yield call(openLastActiveScreenshareSaga);
    }
  }

  yield call(eventBus.sendMessage, 'screenshareStopped', { userId });

  yield call(cleanupScreenshareStreamsSaga, {
    userId,
    feedId,
    streams,
  });
}
